import type { PublicSubscriptionPlan } from "@/lib/subscription-types"
import { formatPlanHeadline, isFreePlan } from "@/lib/subscription-types"

function formatLimitPhrase(value: number | null, unit: string, units?: string): string {
  const plural = units ?? `${unit}s`
  if (value === null) return `unlimited ${plural}`
  return `up to ${value} ${value === 1 ? unit : plural}`
}

export function buildFreeTierLimitSummary(plan: PublicSubscriptionPlan): string {
  const { limits } = plan
  return [
    formatLimitPhrase(limits.included_seats, "teammate", "teammates"),
    formatLimitPhrase(limits.max_projects, "project", "projects"),
    formatLimitPhrase(limits.max_scenarios, "scenario", "scenarios"),
  ].join(", ")
}

export const PUBLIC_LANDING_PLAN_IDS = ["builder", "pro"] as const

export function filterPublicLandingPlans(plans: PublicSubscriptionPlan[]): PublicSubscriptionPlan[] {
  return plans
    .filter((plan) => PUBLIC_LANDING_PLAN_IDS.includes(plan.id as (typeof PUBLIC_LANDING_PLAN_IDS)[number]))
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

/** Public plan label — slug and DB name are aligned (builder, pro). */
export function planDisplayName(plan: { id: string; name?: string }): string {
  return plan.name ?? plan.id
}

export function landingPlanDisplayName(plan: PublicSubscriptionPlan): string {
  return planDisplayName(plan)
}

export function buildLimitedAvailabilityPricingFaqAnswer(): string {
  return `Every tier is free of charge while availability is limited — there's no subscription cost to use ArtlessDev during this period. Paid plans begin at general availability, and we'll give notice before any charges start. See What's next in the app for capabilities still in development.`
}

export interface LimitedAvailabilityPlanPriceDisplay {
  price: string
  period?: string
  /** Muted note under the price, e.g. the standard price that applies at general availability. */
  note?: string
}

export function buildLimitedAvailabilityPlanPriceDisplay(plan: PublicSubscriptionPlan): LimitedAvailabilityPlanPriceDisplay {
  if (isFreePlan(plan)) {
    return { price: "Free" }
  }

  const headline = formatPlanHeadline(plan.priceCents, plan.seatPriceCents, plan.priceInterval)
  return {
    price: "Free",
    note: `${headline.price}${headline.period} at general availability`,
  }
}
